import { PosApiError, POS_BASE } from "@/lib/api/client";
import type { Problem } from "@/lib/api/types";

/**
 * Staff and places, as pos-orchestrator serves them today (`/pos/staff`, `/pos/locations`).
 *
 * <p>Hand-written like `customers.ts` rather than generated: those two operation groups belong to
 * the orchestrator pack, and the pack-client generator only covers the vertical contracts.
 *
 * <p>Both endpoints existed from the day Org &amp; access shipped and nothing called them, so an
 * owner who hired a cashier had no way to give them a login — the screen said "invite someone and
 * they will appear here" beside no way to invite anybody.
 */

/** One person on staff, as the owner holds them. */
export type PosStaff = {
  staffUserId: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  locationId: string | null;
  active: boolean;
  /** The owner's version; a change must carry the one it was read with. */
  version: number;
};

/** One of the tenant's places, as the register holds it. */
export type PosPlace = { locationId: string; name: string; active: boolean };

/** The roles a counter person can hold — user-management's own list (PosStaffCommandService). */
export const COUNTER_ROLES = ["CASHIER", "BRANCH_MANAGER", "CLINICIAN"] as const;
export type CounterRole = (typeof COUNTER_ROLES)[number];

function mutationKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${POS_BASE}${path}`, { ...init, credentials: "include" });
  if (!response.ok) {
    let problem: Problem = { code: `HTTP_${response.status}`, message: response.statusText };
    try {
      problem = (await response.json()) as Problem;
    } catch {
      // A non-JSON body from an upstream keeps the status-derived problem.
    }
    throw new PosApiError(response.status, problem);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const posStaffApi = {
  list: async (): Promise<PosStaff[]> => (await request<{ staff: PosStaff[] }>("/staff")).staff ?? [],

  places: async (): Promise<PosPlace[]> => {
    const answer = await request<PosPlace[] | { locations: PosPlace[] }>("/locations");
    return Array.isArray(answer) ? answer : (answer.locations ?? []);
  },

  /** Hires someone: a login at one place, with the role that decides what they may do. */
  create: (person: { firstName: string; lastName: string; email: string; role: CounterRole; locationId: string }) =>
    request<PosStaff>("/staff", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Idempotency-Key": mutationKey() },
      body: JSON.stringify(person),
    }),

  /**
   * Changes a person's role, their place, or switches them off.
   *
   * <p>`expectedVersion` is the version the row was read with; the owner refuses a stale one rather
   * than overwriting somebody else's change.
   */
  update: (
    staffUserId: string,
    change: { role?: CounterRole; locationId?: string; active?: boolean; expectedVersion: number },
  ) =>
    request<PosStaff>(`/staff/${encodeURIComponent(staffUserId)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", "Idempotency-Key": mutationKey() },
      body: JSON.stringify(change),
    }),
};
